<script setup>
    import { defineProps, defineEmits, computed } from 'vue'
    import { str_slug } from '@/utils/string'

    /** DDIs permitidos no seletor (demais países ficam fora por enquanto). */
    const ALLOWED_COUNTRY_OPTIONS = [
        { value: '54', description: 'Argentina' },
        { value: '55', description: 'Brasil' },
        { value: '595', description: 'Paraguai' },
    ]

    const props = defineProps({
        label: {type: String, default: '', required: true},
        positionLabel: {type: String, default: ''},
        valuePhone: {type: String, default: ''},
        countryCod: {type: String, default: ''},
        blockEdit: {type: Boolean, default: false},
    })

    const emit = defineEmits(['phone-value', 'keyEnter'])

    const inputSlug = str_slug(props.label)
    const countrySelectId = `${inputSlug}-country-cod`

    function updatePhone(evt) {
        emit('phone-value', { phone: evt.target.value })
    }

    function updateCountryCod(evt) {
        emit('phone-value', { cod: evt.target.value })
    }

    const dialCode = computed(() => {
        const raw = String(props.countryCod ?? '55').replace(/\D/g, '')
        return raw || '55'
    })

    /** Se o canal tiver DDI fora da lista restrita, o select mostra Brasil até o usuário escolher. */
    const dialCodeForSelect = computed(() => {
        const raw = dialCode.value
        return ALLOWED_COUNTRY_OPTIONS.some((o) => o.value === raw) ? raw : '55'
    })

    /** Celular PY: 9 dígitos no formato nacional 9XX XXX XXX (sem 0 inicial). */
    const mask = computed(() => {
        if (dialCode.value === '595') {
            return ['### ### ###']
        }
        if (!props.valuePhone) {
            return ['(##) #####-####', '(##) ####-####']
        }
        const value = props.valuePhone
            .slice(0, 7)
            .replaceAll('(', '')
            .replaceAll(')', '')
            .replaceAll(' ', '')

        return value.includes('0800') ? ['#### ### ####'] : ['(##) #####-####', '(##) ####-####']
    })

    const phonePlaceholder = computed(() => {
        if (dialCode.value === '595') {
            return '9XX XXX XXX (celular)'
        }
        return 'DDD + Número do celular'
    })

    function sendMessage() {
        emit('keyEnter')
    }

</script>

<template>
    <div
        :class="positionLabel === 'top' ? 'sm:block' : 'sm:flex'"
        class="space-y-1 px-2 sm:justify-between sm:items-center gap-4 w-full"
    >
        <div>
            <label
                :for="inputSlug"
                class="block text-sm font-medium text-gray-900 dark:text-gray-400"
            >
                {{ label }}
            </label>
        </div>

        <div class="flex items-center w-full input">
            <select
                :id="countrySelectId"
                :value="dialCodeForSelect"
                class="!w-auto max-w-[min(100%,12rem)] min-w-[5.25rem] shrink-0 -mr-px rounded-r-none py-2 pl-2 pr-8"
                style="border-top-right-radius: 0;border-bottom-right-radius: 0;"
                aria-label="Código do país (DDI)"
                :disabled="blockEdit"
                @change="updateCountryCod"
            >
                <option
                    v-for="opt in ALLOWED_COUNTRY_OPTIONS"
                    :key="opt.value"
                    :value="opt.value"
                >
                    +{{ opt.value }} {{ opt.description }}
                </option>
            </select>

            <input
                :id="inputSlug"
                v-mask="mask"
                :value="props.valuePhone"
                class="w-full min-w-0"
                style="border-top-left-radius: 0;border-bottom-left-radius: 0;"
                type="tel"
                :placeholder="phonePlaceholder"
                :disabled="blockEdit"
                @input="updatePhone"
                @keydown.enter.exact.prevent="sendMessage"
            >
        </div>
    </div>
</template>
